import { TranslateFunc } from '@prisjakt/ui-translate/src/translate-func';
import { PriceDisplay } from 'src/components/price-list/types';
import { getDisplayPrice } from '../price-display';
import { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import { OfferPrices, PricePrice } from 'src/types';

export function getCompiledPriceData(
  price: PricePrice | null | undefined,
  offerPrice: OfferPrices | null | undefined,
  priceDisplay: PriceDisplay,
  isFeatured: boolean,
  t: TranslateFunc,
  market: ClientMarket
) {
  if (!price) {
    return { label: null, mainPrice: null, secondaryPrice: null };
  }

  const isMemberPrice = getIsMemberPrice(price, offerPrice, isFeatured);

  const isPromotion = getIsPromotion(price, offerPrice, isFeatured);

  const secondaryPrice = getSecondaryPrice(
    offerPrice,
    priceDisplay,
    isPromotion,
    isMemberPrice
  );

  const hasPriceDate =
    isFeatured &&
    isPromotion &&
    Boolean(secondaryPrice) &&
    Boolean(offerPrice?.price?.endDate);

  const label = getLabel(
    offerPrice,
    priceDisplay,
    hasPriceDate,
    isMemberPrice,
    isFeatured,
    t,
    market
  );

  return {
    label,
    mainPrice: getDisplayPrice(price, priceDisplay),
    secondaryPrice,
    isMemberPrice,
    isPromotion,
  };
}

export function getIsPromotion(
  price: PricePrice | null | undefined,
  offerPrice: OfferPrices | null | undefined,
  featured: boolean
) {
  return featured && pricesAreEqual(price, offerPrice)
    ? offerPrice?.price?.exclShipping !==
        offerPrice?.originalPrice?.exclShipping
    : false;
}

export function getIsMemberPrice(
  price: PricePrice | null | undefined,
  offerPrice: OfferPrices | null | undefined,
  featured: boolean
): boolean {
  return featured && pricesAreEqual(price, offerPrice)
    ? Boolean(offerPrice?.memberPrice)
    : false;
}

export function pricesAreEqual(
  price: PricePrice | null | undefined,
  offerPrice: OfferPrices | null | undefined
): boolean {
  return price?.exclShipping === offerPrice?.price?.exclShipping;
}

export function getLabel(
  offerPrice: OfferPrices | null | undefined,
  priceDisplay: PriceDisplay,
  hasPriceDate: boolean,
  isMemberPrice: boolean,
  isFeatured: boolean,
  t: TranslateFunc,
  market: ClientMarket
) {
  const promotionLabel =
    hasPriceDate && isFeatured
      ? getPromotionLabel(offerPrice, market.localeStandard, t)
      : null;

  const memberLabel = isMemberPrice && isFeatured ? getMemberLabel(t) : null;

  return memberLabel || promotionLabel;
}

export function getSecondaryPrice(
  offerPrice: OfferPrices | null | undefined,
  priceDisplay: PriceDisplay,
  isPromotion: boolean,
  isMemberPrice: boolean
): number | null {
  return offerPrice?.originalPrice && (isPromotion || isMemberPrice)
    ? getDisplayPrice(offerPrice.originalPrice, priceDisplay)
    : null;
}

export function getPromotionLabel(
  offerPrice: OfferPrices | null | undefined,
  locale: string,
  t: TranslateFunc
) {
  if (!offerPrice?.price?.endDate) {
    return null;
  }
  return `${t('product', 'promotions.exp-date-label')} ${parseUTCDate(
    offerPrice!.price!.endDate!,
    locale
  )}`;
}

export function parseUTCDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  const timeDiff = date.getTimezoneOffset() * 60000;
  const adjustedUTCDate = new Date(date.valueOf() + timeDiff);
  return adjustedUTCDate.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  });
}

export function getMemberLabel(t: TranslateFunc) {
  return t('product', 'promotions.members-price');
}
